import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { Label, Input, Row, Col, Button } from 'reactstrap';
import CustomSelect from '../../../Base/Common/CustomSelect';
import { getBindingSelectOptions, FONT_FAMILIES, getInitialPreviewLang, getOrderedTemplateLanguages, normalizeFreeText } from '../templateUtils';

class TextInspector extends Component {
    _textareaRef = React.createRef();

    constructor(props) {
        super(props);
        this.state = { activeLang: getInitialPreviewLang() };
    }

    _update = (field, value) => {
        const { element, onChange } = this.props;
        onChange({ ...element, [field]: value });
    };

    _updateInput = (e) => {
        this._update(e.target.name, e.target.value);
    };

    _updateCheckbox = (e) => {
        this._update(e.target.name, e.target.checked);
    };

    _updateFreeTextLang = (lang, value) => {
        const { element } = this.props;
        const current = normalizeFreeText(element.freeText);
        this._update('freeText', { ...current, [lang]: value });
    };

    _insertAtCursor = (text) => {
        const ta = this._textareaRef.current;
        if (!ta) return;
        const lang = this.state.activeLang;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const current = normalizeFreeText(this.props.element.freeText)[lang] || '';
        const newValue = current.slice(0, start) + text + current.slice(end);
        this._updateFreeTextLang(lang, newValue);
        requestAnimationFrame(() => {
            if (this._textareaRef.current) {
                this._textareaRef.current.selectionStart = start + text.length;
                this._textareaRef.current.selectionEnd = start + text.length;
                this._textareaRef.current.focus();
            }
        });
    };

    render() {
        const { element } = this.props;
        const { activeLang } = this.state;
        const freeTextObj = normalizeFreeText(element.freeText);

        const bindingOptions = [
            { value: null, label: '— Free text —' },
            ...getBindingSelectOptions('text', element.binding),
        ];
        const fontFamilyOptions = FONT_FAMILIES.map(f => ({ value: f, label: f }));
        const alignOptions = [
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right', label: 'Right' },
        ];
        const verticalAlignOptions = [
            { value: 'top', label: 'Top' },
            { value: 'middle', label: 'Middle' },
            { value: 'bottom', label: 'Bottom' },
        ];

        return (
            <div>
                <Row className="mb-2">
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Inspector.Binding" />
                        </Label>
                        <CustomSelect
                            options={bindingOptions}
                            value={bindingOptions.find(o => o.value === element.binding) || bindingOptions[0]}
                            onChange={(opt) => this._update('binding', opt ? opt.value : null)}
                        />
                    </Col>
                </Row>

                {!element.binding && (
                    <Row className="mb-2">
                        <Col>
                            <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                                <FormattedMessage id="TemplateDesigner.Inspector.FreeText" />
                            </Label>
                            {/* Language tabs */}
                            <div style={{ display: 'flex', gap: 2, marginBottom: 4 }}>
                                {getOrderedTemplateLanguages().map(({ code, flagClass, label }) => (
                                    <button
                                        key={code}
                                        type="button"
                                        title={label}
                                        onClick={() => this.setState({ activeLang: code })}
                                        style={{
                                            padding: '3px 6px',
                                            border: '1px solid #ced4da',
                                            borderRadius: 3,
                                            backgroundColor: activeLang === code ? '#e8f0fe' : '#fff',
                                            outline: activeLang === code ? '2px solid #007bff' : 'none',
                                            cursor: 'pointer',
                                            opacity: activeLang === code ? 1 : 0.5,
                                            lineHeight: 1,
                                        }}
                                    >
                                        <span className={flagClass} style={{ fontSize: '1.1rem' }} />
                                    </button>
                                ))}
                            </div>
                            {/* Bullet / symbol insert toolbar */}
                            <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                                {[
                                    { label: '•', title: 'Bullet', insert: '• ' },
                                    { label: '–', title: 'Dash', insert: '– ' },
                                    { label: '→', title: 'Arrow', insert: '→ ' },
                                    { label: '↵', title: 'New line', insert: '\n' },
                                ].map(({ label, title, insert }) => (
                                    <Button
                                        key={label}
                                        size="sm"
                                        color="secondary"
                                        title={title}
                                        onClick={() => this._insertAtCursor(insert)}
                                        style={{ padding: '1px 7px', fontSize: '0.85rem', lineHeight: 1.4 }}
                                    >
                                        {label}
                                    </Button>
                                ))}
                            </div>
                            <textarea
                                ref={this._textareaRef}
                                rows={4}
                                key={activeLang}
                                value={freeTextObj[activeLang] || ''}
                                onChange={(e) => this._updateFreeTextLang(activeLang, e.target.value)}
                                style={{
                                    width: '100%',
                                    fontFamily: 'inherit',
                                    fontSize: '0.85rem',
                                    resize: 'vertical',
                                    border: '1px solid #ced4da',
                                    borderRadius: 4,
                                    padding: '4px 8px',
                                    boxSizing: 'border-box',
                                    lineHeight: 1.5,
                                }}
                            />
                        </Col>
                    </Row>
                )}

                <Row className="mb-2">
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Inspector.FontFamily" />
                        </Label>
                        <CustomSelect
                            options={fontFamilyOptions}
                            value={fontFamilyOptions.find(o => o.value === element.fontFamily) || fontFamilyOptions[0]}
                            onChange={(opt) => this._update('fontFamily', opt ? opt.value : 'Arial')}
                        />
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Inspector.FontSize" />
                        </Label>
                        <Input
                            type="number"
                            name="fontSize"
                            min={6}
                            max={120}
                            value={element.fontSize || 14}
                            onChange={(e) => this._update('fontSize', parseInt(e.target.value, 10) || 14)}
                        />
                    </Col>
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Inspector.Colour" />
                        </Label>
                        <Input
                            type="color"
                            name="colour"
                            value={element.colour || '#333333'}
                            onChange={(e) => this._update('colour', e.target.value)}
                            style={{ height: '38px', padding: '2px 4px' }}
                        />
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col>
                        <Label className="mb-1 d-block font-weight-bold" style={{ fontSize: '0.8rem' }}><FormattedMessage id="TemplateDesigner.Inspector.Style" /></Label>
                        <div className="d-flex" style={{ gap: '8px' }}>
                            <Button
                                size="sm"
                                color={element.bold ? 'primary' : 'secondary'}
                                onClick={() => this._update('bold', !element.bold)}
                            >
                                <strong>B</strong>
                            </Button>
                            <Button
                                size="sm"
                                color={element.italic ? 'primary' : 'secondary'}
                                onClick={() => this._update('italic', !element.italic)}
                            >
                                <em>I</em>
                            </Button>
                            <Button
                                size="sm"
                                color={element.underline ? 'primary' : 'secondary'}
                                onClick={() => this._update('underline', !element.underline)}
                            >
                                <u>U</u>
                            </Button>
                        </div>
                    </Col>
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}><FormattedMessage id="TemplateDesigner.Inspector.LineHeight" /></Label>
                        <Input
                            type="number"
                            name="lineHeight"
                            min={0.8}
                            max={3}
                            step={0.1}
                            value={element.lineHeight ?? 1.2}
                            onChange={(e) => this._update('lineHeight', parseFloat(e.target.value) || 1.2)}
                        />
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}><FormattedMessage id="TemplateDesigner.Inspector.Align" /></Label>
                        <CustomSelect
                            options={alignOptions}
                            value={alignOptions.find(o => o.value === element.textAlign) || alignOptions[0]}
                            onChange={(opt) => this._update('textAlign', opt ? opt.value : 'left')}
                        />
                    </Col>
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}><FormattedMessage id="TemplateDesigner.Inspector.VerticalAlign" /></Label>
                        <CustomSelect
                            options={verticalAlignOptions}
                            value={verticalAlignOptions.find(o => o.value === (element.verticalAlign || 'top')) || verticalAlignOptions[0]}
                            onChange={(opt) => this._update('verticalAlign', opt ? opt.value : 'top')}
                        />
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Inspector.BgColour" />
                        </Label>
                        <div className="d-flex" style={{ gap: '4px' }}>
                            <Input
                                type="color"
                                name="bgColour"
                                value={element.bgColour === 'transparent' || !element.bgColour ? '#ffffff' : element.bgColour}
                                onChange={(e) => this._update('bgColour', e.target.value)}
                                style={{ flex: 1, height: '38px', padding: '2px 4px' }}
                            />
                            <Button
                                size="sm"
                                color="secondary"
                                title="Transparent"
                                onClick={() => this._update('bgColour', 'transparent')}
                                style={{ padding: '2px 8px' }}
                            >
                                ×
                            </Button>
                        </div>
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Inspector.BorderWidth" />
                        </Label>
                        <Input
                            type="number"
                            name="borderWidth"
                            min={0}
                            max={20}
                            value={element.borderWidth ?? 0}
                            onChange={(e) => this._update('borderWidth', parseInt(e.target.value, 10) || 0)}
                        />
                    </Col>
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Inspector.BorderColour" />
                        </Label>
                        <Input
                            type="color"
                            name="borderColour"
                            value={element.borderColour || '#000000'}
                            onChange={(e) => this._update('borderColour', e.target.value)}
                            style={{ height: '38px', padding: '2px 4px' }}
                        />
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Inspector.BorderRadius" />
                        </Label>
                        <Input
                            type="number"
                            name="borderRadius"
                            min={0}
                            max={50}
                            value={element.borderRadius ?? 0}
                            onChange={(e) => this._update('borderRadius', parseInt(e.target.value, 10) || 0)}
                        />
                    </Col>
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Inspector.Padding" />
                        </Label>
                        <Input
                            type="number"
                            name="padding"
                            min={0}
                            max={50}
                            value={element.padding ?? 4}
                            onChange={(e) => this._update('padding', parseInt(e.target.value, 10) || 0)}
                        />
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Inspector.LetterSpacing" />
                        </Label>
                        <Input
                            type="number"
                            name="letterSpacing"
                            min={-2}
                            max={20}
                            step={0.5}
                            value={element.letterSpacing ?? 0}
                            onChange={(e) => this._update('letterSpacing', parseFloat(e.target.value) || 0)}
                        />
                    </Col>
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Inspector.Margin" />
                        </Label>
                        <Input
                            type="number"
                            name="margin"
                            min={0}
                            max={50}
                            value={element.margin ?? 0}
                            onChange={(e) => this._update('margin', parseFloat(e.target.value) || 0)}
                        />
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col>
                        <Label className="mb-1 font-weight-bold" style={{ fontSize: '0.8rem' }}>
                            <FormattedMessage id="TemplateDesigner.Inspector.Shadow" />
                        </Label>
                        <Input
                            type="select"
                            value={element.shadow || 'none'}
                            onChange={(e) => this._update('shadow', e.target.value)}
                        >
                            <option value="none">None</option>
                            <option value="soft">Soft</option>
                            <option value="medium">Medium</option>
                            <option value="strong">Strong</option>
                        </Input>
                    </Col>
                </Row>
            </div>
        );
    }
}

export default TextInspector;
